"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
  Table,
  ColorSwatch,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconX,
  IconRobot,
  IconUser,
  IconCoin,
  IconClock,
  IconHome,
} from "@tabler/icons-react";
import Link from "next/link";

export default function AIVsInPersonColorAnalysisContent() {
  // Sample palette to show what AI provides
  const sampleAIResultColors = [
    "#E0115F", "#4169E1", "#228B22", "#FFD700",
    "#8B008B", "#FF6347", "#20B2AA", "#DDA0DD"
  ];
  const comparisonData = [
    {
      aspect: "Cost",
      ai: "$5-15",
      inPerson: "$150-400+",
    },
    {
      aspect: "Time",
      ai: "Under 1 minute",
      inPerson: "1-2 hours + travel",
    },
    {
      aspect: "Availability",
      ai: "24/7, anywhere",
      inPerson: "Limited by location & schedule",
    },
    {
      aspect: "Objectivity",
      ai: "100% data-driven",
      inPerson: "Varies by consultant",
    },
    {
      aspect: "Personalization",
      ai: "Season + palette + colors to avoid",
      inPerson: "Season + detailed styling advice",
    },
    {
      aspect: "Can Retry",
      ai: "Yes, with different photos",
      inPerson: "Requires new appointment",
    },
  ];

  const aiPros = [
    "Fraction of the cost ($5-15 vs $150-400)",
    "Instant results — no scheduling or waiting",
    "No travel required — do it from home",
    "Objective analysis based on measurable data",
    "Can try multiple photos to confirm results",
    "No pressure from a salesperson",
  ];

  const aiCons = [
    "Can't drape fabrics against your skin in real-time",
    "Photo quality affects accuracy",
    "Less personalized styling conversation",
    "May not catch very subtle coloring nuances",
  ];

  const inPersonPros = [
    "Real-time fabric draping in controlled lighting",
    "Personalized conversation about your style goals",
    "Can ask questions and get immediate answers",
    "May include additional services (makeup tips, wardrobe audit)",
    "Human expertise for edge cases",
  ];

  const inPersonCons = [
    "Expensive ($150-400+ for quality consultants)",
    "Requires scheduling and travel",
    "Quality varies significantly by consultant",
    "Subjective — different consultants may give different results",
    "One-time session — hard to revisit if unsure",
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            AI vs In-Person Color Analysis
          </Title>
          <Text size="lg" c="dimmed" maw={650} mx="auto" mb="xl">
            Both methods can accurately determine your seasonal colors.
            The right choice depends on your budget, time, and what you want from the experience.
          </Text>
        </Box>

        {/* Quick Verdict */}
        <Card withBorder p="xl" radius="md" bg="blue.0">
          <Title order={2} size="h3" mb="md" ta="center">
            The Quick Verdict
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md" bg="white">
              <Group mb="sm">
                <ThemeIcon color="violet" variant="light" size="lg">
                  <IconRobot size={20} />
                </ThemeIcon>
                <Text fw={600}>Choose AI If...</Text>
              </Group>
              <Text size="sm" c="dimmed">
                You want quick, affordable results and are primarily interested in knowing your season
                and best colors. Great for getting started or confirming a suspected season.
              </Text>
            </Paper>
            <Paper withBorder p="lg" radius="md" bg="white">
              <Group mb="sm">
                <ThemeIcon color="pink" variant="light" size="lg">
                  <IconUser size={20} />
                </ThemeIcon>
                <Text fw={600}>Choose In-Person If...</Text>
              </Group>
              <Text size="sm" c="dimmed">
                You want a full styling experience with personalized advice, have the budget for it,
                and enjoy the process of working with a professional.
              </Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Comparison Table */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Side-by-Side Comparison
          </Title>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Aspect</Table.Th>
                <Table.Th>AI Analysis</Table.Th>
                <Table.Th>In-Person Consultation</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {comparisonData.map((row, index) => (
                <Table.Tr key={index}>
                  <Table.Td fw={500}>{row.aspect}</Table.Td>
                  <Table.Td>{row.ai}</Table.Td>
                  <Table.Td>{row.inPerson}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        {/* What AI Analysis Includes - Visual */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Title order={2} mb="md" ta="center">
            What AI Analysis Delivers
          </Title>
          <Text size="md" ta="center" c="dimmed" mb="lg" maw={500} mx="auto">
            For $5-15, here's what you actually get — the same core information an in-person consultant provides.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Paper withBorder p="lg" radius="md" bg="white">
              <Text fw={600} ta="center" mb="sm">Your Season</Text>
              <Box ta="center" py="md" px="sm" bg="blue.1" style={{ borderRadius: 8 }}>
                <Text size="lg" fw={700}>Cool Winter</Text>
                <Text size="xs" c="dimmed">High contrast, cool undertones</Text>
              </Box>
            </Paper>
            <Paper withBorder p="lg" radius="md" bg="white">
              <Text fw={600} ta="center" mb="sm">Your Best Colors</Text>
              <Group justify="center" gap={6}>
                {sampleAIResultColors.map((hex, i) => (
                  <ColorSwatch key={i} color={hex} size={32} radius="md" />
                ))}
              </Group>
              <Text size="xs" c="dimmed" ta="center" mt="sm">60+ colors with hex codes</Text>
            </Paper>
            <Paper withBorder p="lg" radius="md" bg="white">
              <Text fw={600} ta="center" mb="sm">Colors to Avoid</Text>
              <Group justify="center" gap={6}>
                <ColorSwatch color="#FFA500" size={32} radius="md" />
                <ColorSwatch color="#DAA520" size={32} radius="md" />
                <ColorSwatch color="#CD853F" size={32} radius="md" />
                <ColorSwatch color="#D2691E" size={32} radius="md" />
              </Group>
              <Text size="xs" c="dimmed" ta="center" mt="sm">Know what to skip</Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Pros/Cons */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {/* AI Section */}
          <Stack gap="md">
            <Card withBorder p="xl" radius="md">
              <Group mb="md">
                <ThemeIcon color="violet" variant="light" size="lg">
                  <IconRobot size={20} />
                </ThemeIcon>
                <Title order={3} size="h4">
                  AI Color Analysis
                </Title>
              </Group>
              <Text fw={600} c="green" size="sm" mb="xs">
                Pros
              </Text>
              <List size="sm" spacing="xs" mb="md" icon={<IconCheck size={14} color="green" />}>
                {aiPros.map((item, index) => (
                  <List.Item key={index}>{item}</List.Item>
                ))}
              </List>
              <Text fw={600} c="red" size="sm" mb="xs">
                Cons
              </Text>
              <List size="sm" spacing="xs" icon={<IconX size={14} color="red" />}>
                {aiCons.map((item, index) => (
                  <List.Item key={index}>{item}</List.Item>
                ))}
              </List>
            </Card>
          </Stack>

          {/* In-Person Section */}
          <Stack gap="md">
            <Card withBorder p="xl" radius="md">
              <Group mb="md">
                <ThemeIcon color="pink" variant="light" size="lg">
                  <IconUser size={20} />
                </ThemeIcon>
                <Title order={3} size="h4">
                  In-Person Consultation
                </Title>
              </Group>
              <Text fw={600} c="green" size="sm" mb="xs">
                Pros
              </Text>
              <List size="sm" spacing="xs" mb="md" icon={<IconCheck size={14} color="green" />}>
                {inPersonPros.map((item, index) => (
                  <List.Item key={index}>{item}</List.Item>
                ))}
              </List>
              <Text fw={600} c="red" size="sm" mb="xs">
                Cons
              </Text>
              <List size="sm" spacing="xs" icon={<IconX size={14} color="red" />}>
                {inPersonCons.map((item, index) => (
                  <List.Item key={index}>{item}</List.Item>
                ))}
              </List>
            </Card>
          </Stack>
        </SimpleGrid>

        {/* The Hybrid Approach */}
        <Card withBorder p="xl" radius="md" bg="green.0">
          <Title order={2} mb="md" ta="center">
            The Smart Approach: Start with AI
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg">
            Many people use AI color analysis first to get a baseline, then decide if they want
            the full in-person experience.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Paper withBorder p="md" radius="md" bg="white" ta="center">
              <ThemeIcon size={40} radius="xl" color="violet" variant="light" mx="auto" mb="sm">
                <IconCoin size={22} />
              </ThemeIcon>
              <Text fw={600} size="sm" mb="xs">Save Money</Text>
              <Text size="xs" c="dimmed">
                If AI confirms your suspected season, you may not need the expensive consultation
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" bg="white" ta="center">
              <ThemeIcon size={40} radius="xl" color="violet" variant="light" mx="auto" mb="sm">
                <IconClock size={22} />
              </ThemeIcon>
              <Text fw={600} size="sm" mb="xs">Save Time</Text>
              <Text size="xs" c="dimmed">
                Get your answer in minutes instead of waiting for an appointment
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md" bg="white" ta="center">
              <ThemeIcon size={40} radius="xl" color="violet" variant="light" mx="auto" mb="sm">
                <IconHome size={22} />
              </ThemeIcon>
              <Text fw={600} size="sm" mb="xs">Informed Decision</Text>
              <Text size="xs" c="dimmed">
                If you do book in-person, you'll know what questions to ask
              </Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* When In-Person Makes Sense */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            When In-Person Is Worth It
          </Title>
          <Text size="md" ta="center" maw={600} mx="auto" mb="lg">
            Some situations genuinely benefit from an in-person consultation:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Paper withBorder p="md" radius="md">
              <Text fw={600} mb="xs">You have very unusual coloring</Text>
              <Text size="sm" c="dimmed">
                Some rare color combinations benefit from expert eyes that can evaluate subtle nuances.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Text fw={600} mb="xs">You want the full experience</Text>
              <Text size="sm" c="dimmed">
                If you enjoy personalized styling sessions and have the budget, it can be a valuable experience.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Text fw={600} mb="xs">AI results are uncertain</Text>
              <Text size="sm" c="dimmed">
                If AI shows you're between seasons and you want a definitive answer, in-person draping can help.
              </Text>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Text fw={600} mb="xs">You need comprehensive style help</Text>
              <Text size="sm" c="dimmed">
                If you want wardrobe auditing, makeup advice, and personal shopping guidance all together.
              </Text>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Try AI First — It's Quick and Affordable
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Get your seasonal color analysis in under a minute. If you're still unsure after,
              you can always book an in-person session later.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Try AI Color Analysis
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Text size="sm" c="dimmed">
              Learn more about{" "}
              <Text component={Link} href="/how-accurate-is-ai-color-analysis" c="violet" td="underline">
                AI accuracy
              </Text>{" "}
              or{" "}
              <Text component={Link} href="/is-online-color-analysis-worth-it" c="violet" td="underline">
                whether online analysis is worth it
              </Text>
              .
            </Text>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
